import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import Confirmation from '../../components/confirmation/Confirmation';
import ConfirmationContext from './ConfirmationContext';
import type { PendingConfirmationRequest } from './types';
import type {
  ConfirmationContextValue,
  ConfirmationDialogState,
  ConfirmationOptions,
} from '../../components/confirmation/types';

interface ConfirmationProviderProps {
  children: ReactNode;
}

const ConfirmationProvider = ({ children }: ConfirmationProviderProps) => {
  const intl = useIntl();
  const [activeDialog, setActiveDialog] = useState<ConfirmationDialogState | null>(null);
  const activeRequestRef = useRef<PendingConfirmationRequest | null>(null);
  const queueRef = useRef<PendingConfirmationRequest[]>([]);
  const requestIdRef = useRef(0);

  const flushQueue = useCallback(() => {
    if (activeRequestRef.current) {
      return;
    }

    const nextRequest = queueRef.current.shift();
    if (!nextRequest) {
      setActiveDialog(null);
      return;
    }

    activeRequestRef.current = nextRequest;
    setActiveDialog(nextRequest.dialog);
  }, []);

  const settleActiveRequest = useCallback(
    (decision: boolean) => {
      const activeRequest = activeRequestRef.current;
      if (!activeRequest) {
        return;
      }

      activeRequest.resolve(decision);
      activeRequestRef.current = null;
      setActiveDialog(null);
      flushQueue();
    },
    [flushQueue],
  );

  const confirm = useCallback(
    (options: ConfirmationOptions) => {
      return new Promise<boolean>((resolve) => {
        const request: PendingConfirmationRequest = {
          dialog: {
            cancelText: intl.formatMessage({ id: 'cancel' }),
            closeOnBackdropClick: false,
            closeOnEscape: true,
            confirmText: intl.formatMessage({ id: 'confirm' }),
            variant: 'default',
            ...options,
            id: ++requestIdRef.current,
          },
          resolve,
        };

        queueRef.current.push(request);
        flushQueue();
      });
    },
    [flushQueue, intl],
  );

  useEffect(() => {
    return () => {
      activeRequestRef.current?.resolve(false);
      queueRef.current.forEach((request) => request.resolve(false));
      activeRequestRef.current = null;
      queueRef.current = [];
    };
  }, []);

  const contextValue = useMemo<ConfirmationContextValue>(() => ({ confirm }), [confirm]);

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      <Confirmation
        dialog={activeDialog}
        onCancel={() => settleActiveRequest(false)}
        onConfirm={() => settleActiveRequest(true)}
      />
    </ConfirmationContext.Provider>
  );
};

export default ConfirmationProvider;
